import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import { AQLQueryCondition, AQLQueryType } from '@insights_module/adc_ql/types';
import {
  ContextualDropdownOption,
  findOptionByValue,
  getOptions,
  IabCategoriesDropdown,
} from '@insights_module/components/ContextualDropdown';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';

const LANGUAGES = {
  English: {},
  Welsh: {},
  'Spanish (All)': {
    Spanish: {},
    'Spanish (Mexican)': {},
    Catalan: {},
    Galician: {},
  },
  French: {},
  'Dutch (Nederlands)': {},
};

const LANGUAGE_CODES = {
  English: ['en', 'en-US', 'en-GB', 'en-AU', 'en-CA', 'en-NZ', 'en-ZA', 'en-IN'],
  Welsh: ['cy', 'cy-GB'],
  'Spanish (All)': ['es', 'es-MX', 'ca', 'gl'],
  Spanish: ['es'],
  'Spanish (Mexican)': ['es-MX'],
  Catalan: ['ca'],
  Galician: ['gl'],
  French: ['fr', 'fr-FR', 'fr-CA', 'fr-BE', 'fr-CH', 'fr-LU'],
  'Dutch (Nederlands)': ['nl', 'nl-AW', 'nl-BE', 'nl-BQ', 'nl-CW', 'nl-NL', 'nl-SR', 'nl-SX'],
};

const dropdownLanguageOptions = getOptions(LANGUAGES, 1);

export const SegmentCreatorLanguageItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { value, negation } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData, isPresentational } = useSegmentCreatorContext();
  const { dropdownOptions } = useFilterTypeOptions();

  const emptyLanguageOption = {
    value: null,
    label: (
      <Option tw='text-text-and-icon-muted opacity-50'>
        {t('insights.query_builder.language.empty_option')}
      </Option>
    ),
    tierLevel: 0,
    path: [],
  };
  const conditionOptions: IDropdownOption[] = [
    { value: 'any_of', label: <Option>{t('insights.query_builder.conditions.is')}</Option> },
    { value: 'excludes', label: <Option>{t('insights.query_builder.conditions.is_not')}</Option> },
  ];

  // because excludes can be used only with string values, for excluding we will use negation (or wrapping 'any_of' in none query)
  // so if no negation is applied we know that 'any_of' condition is applied
  const getInitialCondition = () => {
    if (negation) {
      return conditionOptions[1];
    }
    return conditionOptions[0];
  };

  // we operate only with arrays because we are using 'any_of' condition even for single language
  // but also one language code can be in multiple arrays (e.g. Spanish-all: ['es', 'es-MX', 'ca', 'gl'] and Spanish: ['es']) so we need to check for array length to know if we are using language group or a single language
  const getInitialValue = () => {
    if (Array.isArray(value) && value.length > 0) {
      return findOptionByValue(
        dropdownLanguageOptions,
        Object.keys(LANGUAGE_CODES).find(
          (key) =>
            LANGUAGE_CODES[key as keyof typeof LANGUAGE_CODES][0] === value[0] &&
            LANGUAGE_CODES[key as keyof typeof LANGUAGE_CODES].length === value.length
        ) || ''
      );
    }

    return null;
  };

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    dropdownOptions.find((item) => item.value === segmentData.type) || dropdownOptions[0]
  );
  const [selectedCondition, setSelectedCondition] =
    useState<IDropdownOption>(getInitialCondition());
  const [selectedLanguage, setSelectedLanguage] = useState<ContextualDropdownOption | null>(
    getInitialValue()
  );
  const [languageAutoFocus, setLanguageAutoFocus] = useState<boolean>(false);

  useEffect(() => {
    if (selectedType) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedCondition && selectedLanguage) {
      const segment = {
        ...segmentData,
        condition: 'any_of' as Extract<AQLQueryCondition, 'any_of'>,
        value: LANGUAGE_CODES[selectedLanguage.value as keyof typeof LANGUAGE_CODES],
        negation: selectedCondition.value === 'excludes',
      };
      pushSegmentData(segment);
    }
  }, [selectedCondition, selectedLanguage]);

  useEffect(() => {
    if (!segmentData.value) {
      setLanguageAutoFocus(true);
    }
  }, []);

  return (
    <div tw='flex gap-1'>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-apple-filter-bg border border-spec-apple-filter-stroke text-spec-apple-filter-text font-semibold`}
        caretStyle={tw`fill-spec-apple-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={isPresentational}
      />

      <Dropdown
        options={conditionOptions}
        value={selectedCondition}
        onChange={(val) => setSelectedCondition(val as IDropdownOption)}
        isPresentational={isPresentational}
      />

      <IabCategoriesDropdown
        emptyOption={emptyLanguageOption}
        options={dropdownLanguageOptions}
        autoFocus={languageAutoFocus}
        isSearchable={false}
        value={selectedLanguage}
        onChange={(val) => setSelectedLanguage(val as ContextualDropdownOption)}
        tw='w-[200px]'
        isPresentational={isPresentational}
        tierLabels={[]}
        dropdownMenuStyle={tw`h-auto`}
      />
    </div>
  );
};
